import { motion } from 'framer-motion';
import { Bell, CheckCircle, EllipsisVertical } from 'lucide-react';
import { useState } from 'react';

import { notificationLinks } from '@/modules/notifications/constants';
import { useNotificationHub } from '@/modules/notifications/hooks/useNotificationHub';
import { useReadAllNotificationsMutation } from '@/modules/notifications/mutations/useReadAllNotificationsMutation';
import { useReadNotificationMutation } from '@/modules/notifications/mutations/useReadNotificationMutation';
import { useUnreadNotificationMutation } from '@/modules/notifications/mutations/useUnreadNotificationMutation';
import { useNotificationsQuery } from '@/modules/notifications/queries/useNotificationsQuery';
import {
  useNotificationsActions,
  useShowUnreadOnly,
} from '@/modules/notifications/store';
import type { NotificationsApi } from '@/modules/notifications/types';
import { Badge } from '@/modules/ui/components/badge/Badge';
import { Button } from '@/modules/ui/components/button/Button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/modules/ui/components/dropdown-menu/DropdownMenu';
import { Heading } from '@/modules/ui/components/heading/Heading';
import { Paragraph } from '@/modules/ui/components/paragraph/Paragraph';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/modules/ui/components/popover/Popover';
import { Switch } from '@/modules/ui/components/switch/Switch';

import { NotificationItem } from './NotificationItem';

export const Notifications = () => {
  const showUnreadOnly = useShowUnreadOnly();
  const [animationKey, setAnimationKey] = useState(0);
  useNotificationHub(() => setAnimationKey((prev) => prev + 1));

  const { toggleShowUnreadOnly } = useNotificationsActions();

  const readNotificationMutation = useReadNotificationMutation();
  const readAllNotificationsMutation = useReadAllNotificationsMutation();
  const unreadNotificationMutation = useUnreadNotificationMutation();
  const notificationsQuery = useNotificationsQuery();

  const notifications = notificationsQuery.data ?? [];
  const filteredNotifications = showUnreadOnly
    ? notifications.filter((notification) => !notification.readUtc)
    : notifications;

  const unreadCount = notifications.filter((n) => !n.readUtc).length;
  const undreadCountDisplayValue = unreadCount > 99 ? '99+' : unreadCount;

  const handleToggleReadStatus = (notificationId: string, isRead: boolean) => {
    if (isRead) {
      unreadNotificationMutation.mutate(notificationId);
    } else {
      readNotificationMutation.mutate(notificationId);
    }
  };

  const buildNotificationLink = (notification: NotificationsApi) => {
    if (!notification.entityAction) return undefined;

    const link = notificationLinks[notification.entityAction];
    const finalLink = notification.entityId
      ? link.replace('{id}', notification.entityId ?? '')
      : link;

    return finalLink;
  };

  const handleMarkAllAsRead = () => {
    readAllNotificationsMutation.mutate();
  };

  return (
    <Popover modal={false}>
      <PopoverTrigger asChild>
        <button
          className="relative flex size-8 cursor-pointer items-center justify-center rounded-full transition-colors hover:bg-gray-100"
          aria-label="Notifications"
        >
          <motion.div
            key={animationKey}
            animate={{
              rotate: [0, -10, 10, -10, 10, -10, 10, 0],
            }}
            transition={{
              duration: 1,
              ease: 'easeInOut',
            }}
          >
            <Bell size={20} className="text-primary-700" />
          </motion.div>

          {unreadCount > 0 && (
            <Badge
              variant="destructive"
              className="absolute -top-1 -right-1 flex size-5 items-center justify-center p-0 text-[10px]"
            >
              {undreadCountDisplayValue}
            </Badge>
          )}
        </button>
      </PopoverTrigger>

      <PopoverContent align="end" sideOffset={16} className="w-md p-0">
        <div className="flex items-center justify-between border-b p-4">
          <Heading variant="h3" className="text-base">
            Notifications
          </Heading>

          <div className="flex items-center gap-2">
            <div className="flex items-center justify-between gap-2">
              <Paragraph variant="caption" className="text-muted-foreground">
                Only show unread
              </Paragraph>
              <Switch
                checked={showUnreadOnly}
                onCheckedChange={toggleShowUnreadOnly}
              />
            </div>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="text-muted-foreground cursor-pointer"
                >
                  <EllipsisVertical size={16} />
                </Button>
              </DropdownMenuTrigger>

              <DropdownMenuContent>
                <DropdownMenuItem onClick={handleMarkAllAsRead}>
                  <CheckCircle size={16} />
                  <span>Mark all as read</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        <div className="max-h-[355px] overflow-y-auto">
          {filteredNotifications.length === 0 ? (
            <Paragraph className="text-muted-foreground p-8 text-center">
              {showUnreadOnly ? 'No unread notifications' : 'No notifications'}
            </Paragraph>
          ) : (
            <div className="divide-y">
              {filteredNotifications.map((notification) => (
                <NotificationItem
                  key={notification.id}
                  notification={notification}
                  href={buildNotificationLink(notification)}
                  onToggleReadStatus={handleToggleReadStatus}
                />
              ))}
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};
